import {map, sortBy} from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {sessionApi} from 'api';
import {InformationModal, CopyText, DataWrapper, Icon, IconName, IconColor} from 'components';
import {useApiData} from 'hooks';
import {OnlineSession, PrevClientSessionSummary, Group, SessionResult, SessionResultStatus} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {getAssignmentName} from 'services';
import {getDuration, getFullSessionResultFormatted} from 'utils/session';
import {formatDate, getParticipantName} from 'utils/transform';

const T_KEYS = TRANSLATION_KEYS.ONLINE_SESSIONS_PAGE.PREV_CLIENT_SESSIONS_MODAL;

type Props = {
    session?: OnlineSession;
    timeZone: string;
    groups: Group[];
    open: boolean;
    onClose: () => void;
};

export const PrevClientSessionsModal: React.FC<Props> = ({session, timeZone, groups, open, onClose}: Props) => {
    const [prevSessionsData, setPrevSessionsData] = useState<{id: string, sessions: PrevClientSessionSummary[]}>(null);
    const {t} = useTranslation();

    const title = useMemo(() => {
        const num = prevSessionsData?.sessions?.length ?? null;
        const numTitle = num ? ` (${num})` : '';
        return `${t(T_KEYS.TITLE)}${numTitle}`;
    }, [t, prevSessionsData]);

    const onSuccess = useCallback((data: PrevClientSessionSummary[]) => {
        setPrevSessionsData({id: session.id, sessions: data});
    }, [session]);

    const onError = useCallback(() => {
        setPrevSessionsData({id: session.id, sessions: []});
    }, [session]);

    const {send: fetchFullSession, loading} = useApiData<PrevClientSessionSummary[], [string]>(sessionApi.getPreviousSessions, {
        onSuccess,
        onError
    });

    const getSessionStatusIconProps = useCallback((prevSession: {result: SessionResult}): {
        name: IconName,
        color: IconColor
    } => {
        if (prevSession.result.status === SessionResultStatus.Success) {
            return ({name: 'check-circle', color: 'success'});
        } else if (prevSession.result.status === SessionResultStatus.Failed) {
            return ({name: 'x-circle', color: 'alert'});
        } else {
            return ({name: 'info', color: 'default-dimmed'});
        }
    }, []);

    useEffect(() => {
        if (!open) {
            setPrevSessionsData(null);
        }
    }, [open]);

    useEffect(() => {
        if (open && session?.id !== prevSessionsData?.id && !loading) {
            fetchFullSession(session.id);
        }
    }, [open, session, prevSessionsData, loading, fetchFullSession]);

    return (
        <InformationModal
            title={title}
            maxWidth='sm'
            fullWidth
            open={open}
            onClose={onClose}
        >
            <DataWrapper isLoading={loading} className='tw-max-w-full tw-overflow-hidden'>
                <div className='tw-max-h-64 tw-overflow-auto'>
                    {map(sortBy(prevSessionsData?.sessions, (s) => -new Date(s.startDate)), (prevSession, index) => {
                        if (prevSession) {
                            const result = getFullSessionResultFormatted(t, prevSession);
                            return (
                                <div key={prevSession.sessionId} className='tw-mb-2'>
                                    <div className='tw-ts-base-bold'>
                                        {index + 1}
                                        {'. '}
                                        <CopyText text={prevSession.sessionId} labelPlacement='left' />
                                    </div>
                                    <div className='tw-flex tw-items-center tw-text-default tw-ml-4 tw-my-2'>
                                        <Icon name='clock' className='tw-mr-2' />
                                        {formatDate(prevSession.startDate, timeZone)}
                                        {' - '}
                                        {formatDate(prevSession.endDate, timeZone)}
                                        {' '}
                                        {`(${getDuration(prevSession.startDate, prevSession.endDate)})`}
                                    </div>

                                    {(prevSession.assignment?.groupId || prevSession.agent) &&
                                        <div className='tw-flex tw-flex-wrap tw-text-default tw-ml-4 tw-mt-2'>
                                            {prevSession.assignment?.groupId &&
                                                <div className='tw-flex tw-mr-2 tw-mb-2'>
                                                    <Icon name='assignment' className='tw-mr-2' />
                                                    {getAssignmentName(groups,
                                                        prevSession.assignment.groupId, prevSession.assignment.subgroupId)}
                                                </div>}
                                            {prevSession.agent &&
                                                <div className='tw-flex tw-mb-2'>
                                                    <Icon name='agent' className='tw-mr-2' />
                                                    {getParticipantName(prevSession.agent.data)}
                                                </div>}
                                        </div>}
                                    {result &&
                                        <div className='tw-flex tw-text-default tw-ml-4 tw-mb-2'>
                                            <Icon {...getSessionStatusIconProps(prevSession)} className='tw-mr-2' />
                                            {result}
                                        </div>}
                                </div>
                            );
                        }
                    })}
                </div>
            </DataWrapper>
        </InformationModal>
    );
};
